import React, { useEffect } from 'react';
import { useFormikContext, Field, FieldArray } from 'formik';
import { Box, Button, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import dayjs from 'dayjs';

import { FormErrorUI, InputUI, SelectUI, TimePickerUi } from '../../Interface';
import { AlertUnitEnum, AlertUnitList, MonthDaysList, WeekDaysList } from '../../../config/alert';

const WeekDayInitials = ['H', 'K', 'S', 'C', 'P', 'S', 'V'];

const sortScheduleTimes = (times) =>
  [...times].sort((firstTime, secondTime) => {
    if (!firstTime && !secondTime) return 0;
    if (!firstTime) return 1;
    if (!secondTime) return -1;
    return dayjs(firstTime).format('HH:mm').localeCompare(dayjs(secondTime).format('HH:mm'));
  });

const AlertFormSchedule = () => {
  const { values, validateField, setFieldValue, errors } = useFormikContext();

  useEffect(() => {
    const unit = values.alertSchedule?.unit;

    if (unit === AlertUnitEnum.CUSTOM_WEEK) {
      if (!(values.alertSchedule?.customWeekdays?.length > 0)) {
        setFieldValue('alertSchedule.customWeekdays', [1]);
      }

      if (!(values.alertSchedule?.customWeekTimes?.length > 0)) {
        setFieldValue('alertSchedule.customWeekTimes', [
          values.alertSchedule?.time ?? dayjs().set('hour', 8).set('minute', 0).set('second', 0).set('millisecond', 0),
        ]);
      }
    } else if (unit === AlertUnitEnum.DAY || unit === AlertUnitEnum.WEEK || unit === AlertUnitEnum.MONTH) {
      if (!values.alertSchedule?.time) {
        setFieldValue(
          'alertSchedule.time',
          values.alertSchedule?.customWeekTimes?.[0] ??
            dayjs().set('hour', 8).set('minute', 0).set('second', 0).set('millisecond', 0)
        );
      }
    } else {
      setFieldValue('alertSchedule.time', null);
    }

    if (unit !== AlertUnitEnum.WEEK && values.alertSchedule?.weekday != null) {
      setFieldValue('alertSchedule.weekday', null);
    }

    if (
      unit === AlertUnitEnum.WEEK &&
      (values.alertSchedule?.weekday === null ||
        values.alertSchedule?.weekday === undefined ||
        values.alertSchedule?.weekday === '')
    ) {
      setFieldValue('alertSchedule.weekday', 1);
    }

    if (unit !== AlertUnitEnum.MONTH && values.alertSchedule?.monthDay != null) {
      setFieldValue('alertSchedule.monthDay', null);
    }

    if (
      unit === AlertUnitEnum.MONTH &&
      (values.alertSchedule?.monthDay === null ||
        values.alertSchedule?.monthDay === undefined ||
        values.alertSchedule?.monthDay === '')
    ) {
      setFieldValue('alertSchedule.monthDay', 1);
    }
  }, [values.alertSchedule?.unit]);

  return (
    <Box sx={{ width: '100%', overflowX: 'auto' }} pt={1}>
      <Box
        elevation={1}
        sx={{
          overflow: 'hidden',
        }}
      >
        <Typography variant="body2" color="text.secondary" fontWeight={600} fontSize={'1rem'} my={1}>
          Gyakoriság
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={4} container justifyContent="flex-end" alignItems="center">
            <Typography variant="body2" color="text.secondary">
              Minden
            </Typography>
          </Grid>
          <Grid item xs={12} md={3}>
            <Field
              type="number"
              name="alertSchedule.every"
              as={InputUI}
              fullWidth
              variant="standard"
              inputProps={{ min: 1 }}
              helperText={<FormErrorUI message={errors.alertSchedule?.every} />}
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <Field
              name="alertSchedule.unit"
              component={SelectUI}
              fullWidth
              option={AlertUnitList}
              value={values.alertSchedule.unit ?? ''}
              variant="standard"
              onChange={(e) => {
                const value = e.target.value;
                setFieldValue('alertSchedule.unit', value ?? null).then(() => {
                  validateField('alertSchedule.unit');
                });
              }}
              helperText={<FormErrorUI message={errors.alertSchedule?.unit} />}
            />
          </Grid>
        </Grid>
        {values.alertSchedule?.unit === AlertUnitEnum.WEEK && (
          <Grid container spacing={2}>
            <Grid item xs={12} md={4} container justifyContent="flex-end" alignItems="center">
              <Typography variant="body2" color="text.secondary">
                Hét napja
              </Typography>
            </Grid>
            <Grid item xs={12} md={8}>
              <Field
                name="alertSchedule.weekday"
                component={SelectUI}
                fullWidth
                option={WeekDaysList}
                value={values.alertSchedule.weekday ?? ''}
                variant="standard"
                onChange={(e) => {
                  const value = e.target.value;
                  setFieldValue('alertSchedule.weekday', value ?? null).then(() => {
                    validateField('alertSchedule.weekday');
                  });
                }}
                helperText={<FormErrorUI message={errors.alertSchedule?.weekday} />}
              />
            </Grid>
          </Grid>
        )}

        {values.alertSchedule?.unit === AlertUnitEnum.CUSTOM_WEEK && (
          <Grid container spacing={2} mt={0}>
            <Grid item xs={12} md={4} container justifyContent="flex-end" alignItems="center">
              <Typography variant="body2" color="text.secondary">
                Hét napjai
              </Typography>
            </Grid>
            <Grid item xs={12} md={8}>
              <Box display="flex" gap={0.75} flexWrap="nowrap" overflow="auto" pb={0.5}>
                {WeekDaysList.map((day, index) => {
                  const selected = values.alertSchedule?.customWeekdays?.includes(day.value) ?? false;

                  return (
                    <Tooltip key={day.value} title={day.label} arrow>
                      <Button
                        type="button"
                        variant={selected ? 'contained' : 'outlined'}
                        color="primary"
                        aria-label={day.label}
                        aria-pressed={selected}
                        onClick={() => {
                          const selectedDays = values.alertSchedule?.customWeekdays ?? [];
                          const weekdays = selected
                            ? selectedDays.filter((value) => value !== day.value)
                            : [...new Set([...selectedDays, day.value])];
                          setFieldValue('alertSchedule.customWeekdays', weekdays).then(() => {
                            validateField('alertSchedule.customWeekdays');
                          });
                        }}
                        sx={{ minWidth: 36, width: 36, height: 36, p: 0, borderRadius: '50%', flex: '0 0 auto' }}
                      >
                        {WeekDayInitials[index]}
                      </Button>
                    </Tooltip>
                  );
                })}
              </Box>
              <Button
                type="button"
                variant="text"
                onClick={() => {
                  const selectedDays = values.alertSchedule?.customWeekdays ?? [];
                  setFieldValue(
                    'alertSchedule.customWeekdays',
                    [...new Set([...selectedDays, 1, 2, 3, 4, 5])]
                  ).then(
                    () => validateField('alertSchedule.customWeekdays')
                  );
                }}
                sx={{ mt: 0.5 }}
              >
                Hétköznapok
              </Button>
              <FormErrorUI message={errors.alertSchedule?.customWeekdays} />
            </Grid>
          </Grid>
        )}

        {values.alertSchedule?.unit === AlertUnitEnum.MONTH && (
          <Grid container spacing={2}>
            <Grid item xs={12} md={4} container justifyContent="flex-end" alignItems="center">
              <Typography variant="body2" color="text.secondary">
                Hónap napja
              </Typography>
            </Grid>
            <Grid item xs={12} md={8}>
              <Field
                type="number"
                name="alertSchedule.monthDay"
                as={SelectUI}
                fullWidth
                option={MonthDaysList}
                variant="standard"
                value={values.alertSchedule.monthDay ?? ''}
                onChange={(e) => {
                  const value = e.target.value;
                  setFieldValue('alertSchedule.monthDay', value ?? null).then(() => {
                    validateField('alertSchedule.monthDay');
                  });
                }}
                helperText={<FormErrorUI message={errors.alertSchedule?.monthDay} />}
              />
            </Grid>
          </Grid>
        )}

        {values.alertSchedule?.unit === AlertUnitEnum.CUSTOM_WEEK ? (
          <Grid container spacing={2} mt={0}>
            <Grid item xs={12} md={4} container justifyContent="flex-end" alignItems="flex-start" pt={3}>
              <Typography variant="body2" color="text.secondary">
                Időpontok
              </Typography>
            </Grid>
            <Grid item xs={12} md={8}>
              <FieldArray name="alertSchedule.customWeekTimes">
                {({ push }) => (
                  <Box>
                    {(values.alertSchedule?.customWeekTimes ?? []).map((time, index) => (
                      <Box key={index} display="flex" alignItems="flex-start" gap={1} mb={1}>
                        <TimePickerUi
                          value={time ? dayjs(time) : null}
                          onChange={(newValue) => {
                            setFieldValue(`alertSchedule.customWeekTimes.${index}`, newValue ?? null).then(() => {
                              validateField('alertSchedule.customWeekTimes');
                            });
                          }}
                          onAccept={(newValue) => {
                            const times = [...values.alertSchedule.customWeekTimes];
                            times[index] = newValue ?? null;
                            setFieldValue('alertSchedule.customWeekTimes', sortScheduleTimes(times));
                          }}
                          helperText={
                            <FormErrorUI
                              message={
                                Array.isArray(errors.alertSchedule?.customWeekTimes)
                                  ? errors.alertSchedule.customWeekTimes[index]
                                  : undefined
                              }
                            />
                          }
                        />
                        <IconButton
                          type="button"
                          color="error"
                          aria-label="Időpont törlése"
                          onClick={() => {
                            const times = values.alertSchedule.customWeekTimes.filter(
                              (_time, timeIndex) => timeIndex !== index
                            );
                            setFieldValue('alertSchedule.customWeekTimes', times).then(() => {
                              validateField('alertSchedule.customWeekTimes');
                            });
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    ))}
                    {typeof errors.alertSchedule?.customWeekTimes === 'string' && (
                      <FormErrorUI message={errors.alertSchedule.customWeekTimes} />
                    )}
                    <Button type="button" startIcon={<AddIcon />} onClick={() => push(null)}>
                      Időpont hozzáadása
                    </Button>
                  </Box>
                )}
              </FieldArray>
            </Grid>
          </Grid>
        ) : values.alertSchedule?.unit === AlertUnitEnum.DAY ||
          values.alertSchedule?.unit === AlertUnitEnum.WEEK ||
          values.alertSchedule?.unit === AlertUnitEnum.MONTH ? (
          <Grid container spacing={2}>
            <Grid item xs={12} md={4} container justifyContent="flex-end" alignItems="center">
              <Typography variant="body2" color="text.secondary">
                Időpont
              </Typography>
            </Grid>
            <Grid item xs={12} md={8}>
              <Field
                name="alertSchedule.time"
                component={TimePickerUi}
                fullWidth
                value={values.alertSchedule.time ? dayjs(values.alertSchedule.time) : null}
                variant="standard"
                onChange={(newValue) => {
                  setFieldValue('alertSchedule.time', newValue ?? null).then(() => {
                    validateField('alertSchedule.time');
                  });
                }}
                helperText={<FormErrorUI message={errors.alertSchedule?.time} />}
              />
            </Grid>
          </Grid>
        ) : null}
      </Box>
    </Box>
  );
};

export default AlertFormSchedule;
